// ResultHistoryPanel — the Result History top-level tab. Lists autosaved query
// results (timestamp + SQL preview) in a sidebar; selecting one shows the full
// SQL in a read-only CodeMirror (no editing/focus) above a read-only data grid
// rendered from the stored CSV. The result is copyable (grid right-click) and
// downloadable (CSV), but never editable.
import { createSignal, createResource, For, Show } from 'solid-js';
import Grid from '../components/Grid.jsx';
import CodeMirrorSQL from '../components/CodeMirrorSQL.jsx';
import { listResults, getResult, deleteResult, resultDownloadUrl } from '../lib/resulthistory.js';
import { toast } from '../lib/toast.js';

const fmtTime = (unixSec) => {
  if (!unixSec) return '';
  try { return new Date(unixSec * 1000).toLocaleString(); } catch { return String(unixSec); }
};
const preview = (sql) => {
  const s = String(sql || '').replace(/\s+/g, ' ').trim();
  return s.length > 90 ? s.slice(0, 90) + '…' : s;
};

export default function ResultHistoryPanel() {
  const [results, { refetch }] = createResource(listResults);
  const [selId, setSelId] = createSignal(null);
  const [detail] = createResource(selId, (id) => (id ? getResult(id) : null));

  const onDelete = async () => {
    const d = detail();
    if (!d) return;
    if (!window.confirm('Delete this saved result?\n\nThe stored CSV file is removed. This cannot be undone.')) return;
    try {
      await deleteResult(d.id);
      setSelId(null);
      refetch();
      toast.success('Result deleted.');
    } catch (e) {
      toast.error('Delete failed: ' + (e?.message || e));
    }
  };

  return (
    <div class="page rh-page">
      <header class="page-header">
        <div>
          <h1>Result History</h1>
          <p class="muted">Autosaved query-result snapshots — read-only. Enable per tab via ⚙ Options → Autosave results.</p>
        </div>
      </header>

      <div class="rh-body">
        <div class="rh-list">
          <Show when={(results() || []).length > 0}
                fallback={<div class="muted" style="padding:14px;font-size:12px;">No saved results yet. Turn on <b>Autosave results</b> in a query tab's ⚙ Options menu.</div>}>
            <For each={results()}>
              {(r) => (
                <div class="rh-item" classList={{ active: selId() === r.id }} onClick={() => setSelId(r.id)}>
                  <div class="rh-item-top">
                    <span class="rh-time">{fmtTime(r.createdAt)}</span>
                    <span class="spacer" />
                    <span class="muted mono" style="font-size:11px;">{r.rowCount} × {r.colCount}</span>
                  </div>
                  <div class="rh-item-sql mono">{preview(r.sqlText)}</div>
                  <Show when={r.database}><div class="rh-item-db muted mono">{r.database}</div></Show>
                </div>
              )}
            </For>
          </Show>
        </div>

        <div class="rh-main">
          <Show when={detail()} fallback={<div class="muted" style="padding:24px;">Select a result on the left to view it.</div>}>
            <div class="rh-toolbar">
              <span class="muted mono" style="font-size:12px;">{fmtTime(detail().createdAt)} · {detail().rowCount} rows</span>
              <span class="spacer" />
              <a class="btn btn-sm btn-ghost" href={resultDownloadUrl(detail().id)} download={`result-${detail().id}.csv`}>⬇ Download CSV</a>
              <button class="btn btn-sm btn-danger" onClick={onDelete}>🗑 Delete</button>
            </div>
            <div class="rh-sql">
              <CodeMirrorSQL value={detail().sql || ''} readOnly={true} maxHeight="160px" lineWrap={true} />
            </div>
            <div class="rh-grid">
              <Grid
                columns={detail().columns || []}
                rows={detail().rows || []}
                totalRows={(detail().rows || []).length}
                selectable={true}
              />
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}
